import { useState, useEffect } from 'react';
import NoiseMatrixEditor from '@/components/spots-admin/NoiseMatrixEditor';
import type { NoiseMatrix } from '@/utils/noiseMatrix';
import AdminAuthGate from '@/components/AdminAuthGate';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Star, ExternalLink, Check } from 'lucide-react';

const API_BASE = 'https://spotsvc.up.railway.app';

const STEPS = ['Search', 'Preview', 'Details'] as const;

const CATEGORIES = [
  { label: 'Cafe', value: 'cafe' },
  { label: 'Gym', value: 'gym' },
  { label: 'Hotel Lobby', value: 'hotel_lobby' },
  { label: 'Coworking', value: 'coworking' },
  { label: 'Library', value: 'library' },
  { label: 'Restaurant', value: 'restaurant' },
  { label: 'Pod', value: 'pod' },
  { label: 'Other', value: 'other' },
];

const ACCESS_TYPES = [
  { label: 'Free to enter', value: 'free' },
  { label: 'Purchase required', value: 'purchase_required' },
  { label: 'Members only', value: 'members_only' },
];


interface Suggestion {
  place_id: string;
  text: string;
  secondary_text: string;
}

interface PlaceDetails {
  name: string;
  formatted_address: string;
  rating: number | null;
  user_rating_count: number | null;
  website_uri: string | null;
  editorial_summary: string | null;
  business_status: string | null;
  photos: string[];
}

const AddSpot = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [placeId, setPlaceId] = useState('');

  // Step 1
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [searching, setSearching] = useState(false);

  // Step 2
  const [place, setPlace] = useState<PlaceDetails | null>(null);
  const [loadingPlace, setLoadingPlace] = useState(false);

  // Step 3
  const [category, setCategory] = useState('');
  const [accessType, setAccessType] = useState('');
  const [wifiAvailable, setWifiAvailable] = useState(true);
  const [powerOutlets, setPowerOutlets] = useState(true);
  const [noiseMatrix, setNoiseMatrix] = useState<NoiseMatrix | null>(null);
  const [description, setDescription] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/google-autocomplete?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Fetch place details
  useEffect(() => {
    if (step !== 1 || !placeId) return;
    setLoadingPlace(true);
    setPlace(null);
    fetch(`${API_BASE}/admin/google-place/${placeId}`)
      .then(r => r.json())
      .then(data => setPlace(data.place))
      .catch(() => toast.error('Failed to load place details'))
      .finally(() => setLoadingPlace(false));
  }, [step, placeId]);

  const handleSelectSuggestion = (s: Suggestion) => {
    setPlaceId(s.place_id);
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!category || !accessType) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    const body: Record<string, unknown> = {
      google_place_id: placeId,
      category,
      access_type: accessType,
      wifi_available: wifiAvailable,
      power_outlets: powerOutlets,
    };
    if (noiseMatrix) body.noise_matrix = noiseMatrix;
    if (description.trim()) body.description = description.trim();
    if (adminNotes.trim()) body.admin_notes = adminNotes.trim();

    try {
      const res = await fetch(`${API_BASE}/admin/spots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 201) {
        toast.success('Spot added successfully');
        navigate('/spots/view');
      } else if (res.status === 409) {
        toast.error('This spot has already been added.');
      } else {
        const data = await res.json().catch(() => null);
        toast.error(data?.message || 'Something went wrong.');
      }
    } catch {
      toast.error('Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors',
                    i < step
                      ? 'bg-primary text-primary-foreground border-primary'
                      : i === step
                        ? 'border-primary text-primary bg-background'
                        : 'border-muted text-muted-foreground bg-background'
                  )}
                >
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span
                  className={cn(
                    'text-xs mt-1.5 font-medium',
                    i === step ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 flex-1 -mt-4',
                    i < step ? 'bg-primary' : 'bg-muted'
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 — Search */}
        {step === 0 && (
          <div>
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search for a place…"
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-base"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-3.5 w-5 h-5 animate-spin text-muted-foreground" />
              )}
            </div>
            {suggestions.length > 0 && (
              <div className="mt-2 rounded-xl border border-input bg-card overflow-hidden shadow-sm">
                {suggestions.map(s => (
                  <button
                    key={s.place_id}
                    onClick={() => handleSelectSuggestion(s)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-input last:border-b-0"
                  >
                    <div className="text-sm font-medium text-foreground">{s.text}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.secondary_text}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Preview */}
        {step === 1 && (
          <div>
            {loadingPlace ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : place ? (
              <div className="rounded-2xl border border-input bg-card overflow-hidden shadow-sm">
                {/* Photos hidden — API now returns photo_references (tokens), not URLs */}
                <div className="p-5 space-y-3">
                  <h2 className="text-xl font-bold text-foreground">{place.name}</h2>
                  <p className="text-sm text-muted-foreground">{place.formatted_address}</p>

                  {place.rating != null && (
                    <div className="flex items-center gap-1.5 text-sm text-foreground">
                      <Star className="w-4 h-4 fill-current text-primary" />
                      <span className="font-semibold">{place.rating}</span>
                      {place.user_rating_count != null && (
                        <span className="text-muted-foreground">({place.user_rating_count})</span>
                      )}
                    </div>
                  )}

                  {place.website_uri && (
                    <a
                      href={place.website_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Website
                    </a>
                  )}

                  {place.editorial_summary && (
                    <p className="text-sm text-foreground leading-relaxed">{place.editorial_summary}</p>
                  )}

                  {place.business_status && (
                    <span
                      className={cn(
                        'inline-block text-xs font-medium px-2.5 py-1 rounded-full',
                        place.business_status === 'OPERATIONAL'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {place.business_status}
                    </span>
                  )}
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3 mt-6">
              <Button variant="ghost" onClick={() => { setStep(0); setPlace(null); }}>
                Back
              </Button>
              <Button onClick={() => setStep(2)} disabled={!place}>
                Looks good, continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Details */}
        {step === 2 && (
          <div className="space-y-5">
            {/* Category */}
            <label className="block">
              <span className="text-sm font-medium text-foreground">Category *</span>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                required
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select…</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>

            {/* Access Type */}
            <label className="block">
              <span className="text-sm font-medium text-foreground">Access type *</span>
              <select
                value={accessType}
                onChange={e => setAccessType(e.target.value)}
                required
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select…</option>
                {ACCESS_TYPES.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </label>

            {/* Toggles */}
            <div className="flex items-center justify-between py-1">
              <span className="text-sm font-medium text-foreground">WiFi available</span>
              <button
                type="button"
                onClick={() => setWifiAvailable(v => !v)}
                className={cn(
                  'relative w-11 h-6 rounded-full transition-colors',
                  wifiAvailable ? 'bg-primary' : 'bg-muted'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
                    wifiAvailable && 'translate-x-5'
                  )}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm font-medium text-foreground">Power outlets</span>
              <button
                type="button"
                onClick={() => setPowerOutlets(v => !v)}
                className={cn(
                  'relative w-11 h-6 rounded-full transition-colors',
                  powerOutlets ? 'bg-primary' : 'bg-muted'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
                    powerOutlets && 'translate-x-5'
                  )}
                />
              </button>
            </div>

            {/* Noise Matrix */}
            <NoiseMatrixEditor value={noiseMatrix} onChange={setNoiseMatrix} />

            {/* Description */}
            <label className="block">
              <span className="text-sm font-medium text-foreground">Description (shown to users)</span>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </label>

            {/* Admin Notes */}
            <label className="block">
              <span className="text-sm font-medium text-foreground">Admin notes (internal only)</span>
              <textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                rows={2}
                className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </label>

            <div className="flex items-center gap-3 pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Spot
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function AddSpotProtected() {
  return (
    <AdminAuthGate>
      <AddSpot />
    </AdminAuthGate>
  );
}

export default AddSpotProtected;
